import { getConfig, klinikenApi } from "@/api";
import {
    baseActions,
    BaseActionsProps,
    baseMutations,
    baseState,
    BaseState,
    fetchItems,
    FilterParams,
} from "@/store/modules/helpers/baseState";
import {
    subsetGetter,
    SubsetGetterProps,
    Subsets,
    subsetSetter,
    subsetUpdateSetter,
} from "@/store/modules/helpers/baseSubset";
import { resetDelayedResetTimeout } from "@/store/modules/helpers/delayedReset";
import { Vardplatsbestallning, VardplatsbestallningStryk } from "@/tabs/Vardplatsbokning/types";
import cloneDeep from "lodash/cloneDeep";

type State = BaseState & {
    vardplatsbestallningar: Map<string | number, Vardplatsbestallning>;
};

type Commit = (
    action: string,
    payload: unknown,
    options?: {
        root?: boolean;
    }
) => void;

type Getters = {
    vardplatsbestallningById: (data: { id: number | string }) => Vardplatsbestallning | null;
};

type AbilityCan = (args: { action: string; subject: string }) => boolean;

const filterParamConverter = ({ filterParams }: { filterParams: FilterParams }) => {
    return Object.fromEntries(
        Object.entries(filterParams).map(([key, value]) => {
            if (key === "show_strukna") {
                return [`status${value ? "__any" : "__ne"}`, "Struken"];
            }

            return [key, value];
        })
    );
};

/**
 * Varje default state ska innehålla BaseState + det som är specifikt för den här modulen.
 * Se helpers/Basestate
 */
const getDefaultState: () => State = () => {
    return {
        vardplatsbestallningar: new Map(),
        ...cloneDeep(baseState),
    };
};

const moduleName = "vardplatsbestallningar";

export default {
    namespaced: true,
    state: getDefaultState(),
    getters: {
        /**
         * Den här ska inte anropas direkt. Hämtning ska göras via actionen (fetchVardplatsbestallning)
         */
        vardplatsbestallningar: (state: State) => [...state.vardplatsbestallningar.values()],
        /**
         * Anropar en generisk getter för hämtning av subset. computed i en komponent som lyssnar på denna getter
         * är förmodligen vad du vill ha i din komponent.
         * Se helpers/baseSubset
         */
        vardplatsbestallningSubset: (state: State) => (props: SubsetGetterProps<State>) =>
            subsetGetter<State>({
                ...props,
                state,
                collectionName: moduleName,
            }),
        /**
         * Om du bara vill hämta ett objekt vid ett enda tillfälle så ska det göras via actionen och inte här
         * (getVardplatsbestallningById, som hämtar från BE om objektet inte finns i store).
         * Om du vill lyssna på förändringar på ett enskilt objekt så vill du troligen lyssna på den här i en
         * computed i din komponent
         */
        vardplatsbestallningById:
            (state: State) =>
            ({ id }: { id?: number | string }) => {
                if (!id) {
                    return null;
                }

                return state.vardplatsbestallningar.get(id);
            },
    },
    /**
     * Mutations ska alltid innehålla baseMutations + det som är specifikt för modulen
     */
    mutations: {
        ...baseMutations({
            reset: (state: State) => {
                Object.assign(state, getDefaultState());

                resetDelayedResetTimeout({ moduleName });
            },
        }),
        /**
         * Tillägg av nya objekt i store anropar i sin tur en generisk setter. Den här ska inte anropas direkt utan
         * istället gå via actionen (createVardplatsbestallning)
         * Se helpers/subset
         */
        addVardplatsbestallningar: (
            state: State,
            {
                offset,
                subsetName = "default",
                vardplatsbestallningar = [],
            }: {
                offset: number;
                subsetName: keyof Subsets;
                vardplatsbestallningar: Vardplatsbestallning[];
            }
        ) =>
            subsetSetter<State, Vardplatsbestallning>({
                offset,
                subsetName,
                state,
                filterParamConverter,
                collectionName: moduleName,
                items: vardplatsbestallningar,
            }),
        /**
         * Uppdatering av objekt i store anropar i sin tur en generisk setter. Den här ska inte anropas direkt utan
         * istället gå via actionen (updateVardplatsbestallning)
         * Se helpers/subset
         */
        updateVardplatsbestallningar: (
            state: State,
            {
                vardplatsbestallningar,
                offset,
                subsetName,
            }: {
                vardplatsbestallningar: Vardplatsbestallning[];
                offset: number;
                subsetName: keyof Subsets;
            } // TODO subsetName kommer skickas in i framtiden
        ) =>
            subsetUpdateSetter<State, Vardplatsbestallning>({
                offset,
                state,
                filterParamConverter,
                collectionName: moduleName,
                items: vardplatsbestallningar,
            }),
    },
    /**
     * Actions ska alltid innehålla baseActions + det som är specifikt för modulen. Den returnerar resultatet,
     * men det du troligen vill göra är att lyssna på
     */
    actions: {
        ...baseActions({ moduleName }),
        getVardplatsbestallningById: async (
            {
                getters,
                commit,
                state,
            }: BaseActionsProps<State> & {
                getters: Getters;
            },
            {
                id,
                addAsNew,
                addAsUpdate,
                config = {},
            }: {
                id: number | string;
                // Ska hämtade datan stoppas in i newlyCreated subset?
                addAsNew?: boolean;
                /*
                    Ska hämtningen läggas till som newlyUpdated?
                    Existerande data kommer att ignoreras i store och en ny hämtning från BE kommer att göras
                 */
                addAsUpdate?: boolean;
                config?: {
                    tabbnamn?: string;
                };
            }
        ) => {
            const foundBestallning = getters.vardplatsbestallningById({ id });

            if (!addAsUpdate && (foundBestallning || !id)) {
                if (foundBestallning && addAsNew) {
                    commit("addVardplatsbestallningar", {
                        offset: 0,
                        vardplatsbestallningar: [foundBestallning],
                        subsetName: "newlyCreated",
                    });
                }

                return foundBestallning;
            }

            try {
                const { data } = await klinikenApi.get<{
                    vardplatsbestallning: Vardplatsbestallning;
                }>(`slutenvard/vardplatsbestallningar/${id}`, getConfig(config));

                if (data) {
                    commit("addVardplatsbestallningar", {
                        offset: 0,
                        vardplatsbestallningar: [data],
                        subsetName: addAsNew ? "newlyCreated" : id,
                    });

                    if (addAsUpdate) {
                        commit("updateVardplatsbestallningar", {
                            subsetName: "newlyUpdated",
                            vardplatsbestallningar: [data],
                            offset: Object.keys(state.subsets.newlyUpdated.ids).length,
                        });
                    }

                    return data;
                }

                return null;
            } catch (error) {
                console.error(error);

                throw error;
            }
        },
        /**
         * Hämta slutenvårdsbesök från state eller från backend och stoppa in i state. Den returnerar inte resultatet.
         * Det du troligen vill göra är att skapa en computed som lyssnar på subsetgettern.
         * Den skapar ett nytt subset om inskickade filteringParams inte redan finns i state.
         * @param limit mängden objekt du vill få tillbaka i resultatet
         * @param page nummer för page. Används för att räkna ut offset som skickas till BE
         * @param filterParams alla sök och filtreringsparametrar. "ordering" är för sortering
         */
        fetchVardplatsbestallningar: async (
            { commit, state }: BaseActionsProps<State>,
            {
                limit = 20,
                page = 1,
                filterParams = {},
                config = {},
            }: {
                limit?: number;
                offset?: number;
                page?: number;
                filterParams?: FilterParams;
                config?: {
                    tabbnamn?: string;
                };
            }
        ) => {
            try {
                const {
                    subsetName,
                    results,
                    total,
                    nextPage,
                    offset: resultOffset,
                } = await fetchItems<State, Vardplatsbestallning>({
                    filterParams,
                    state,
                    limit,
                    page,
                    collectionName: moduleName,
                    apiUrl: "slutenvard/vardplatsbestallningar/",
                    config,
                });

                if (!results) {
                    return {
                        subsetName,
                        total,
                        nextPage,
                    };
                }

                commit("addVardplatsbestallningar", {
                    offset: resultOffset,
                    subsetName,
                    vardplatsbestallningar: results,
                });

                return {
                    results,
                    total,
                    nextPage,
                    subsetName,
                };
            } catch (error) {
                console.error(error);
                throw error;
            }
        },
        createVardplatsbestallning: async (
            { commit, state, dispatch, rootGetters }: BaseActionsProps<State>,
            {
                vardplatsbestallning,
                config = {},
            }: {
                vardplatsbestallning: Vardplatsbestallning;
                config?: {
                    tabbnamn?: string;
                };
            }
        ) => {
            try {
                const response = await klinikenApi.post(
                    "slutenvard/vardplatsbestallningar/",
                    vardplatsbestallning,
                    getConfig(config)
                );

                const data = await dispatch("getVardplatsbestallningById", {
                    id: response.data.id,
                    addAsNew: true,
                });

                commit("addVardplatsbestallningar", {
                    offset: Object.keys(state.subsets.newlyCreated.ids).length,
                    subsetName: "newlyCreated",
                    vardplatsbestallningar: [data],
                });

                if (
                    (rootGetters["ability/can"] as AbilityCan)({
                        action: "read",
                        subject: "slutenvardsbesok",
                    }) &&
                    response.data?.slutenvardsbesok
                ) {
                    await dispatch(
                        "slutenvardsbesok/getSlutenvardsbesokById",
                        {
                            id: response.data.slutenvardsbesok,
                            addAsNew: true,
                        },
                        { root: true }
                    );
                }
            } catch (error) {
                console.error(error);

                throw error;
            }
        },
        updateVardplatsbestallning: async (
            { commit, state, dispatch, rootGetters }: BaseActionsProps<State>,
            {
                vardplatsbestallning,
                config = {},
            }: {
                vardplatsbestallning: Vardplatsbestallning;
                config?: {
                    tabbnamn?: string;
                };
            }
        ) => {
            try {
                const response = await klinikenApi.put(
                    `slutenvard/vardplatsbestallningar/${vardplatsbestallning.id}/`,
                    vardplatsbestallning,
                    getConfig(config)
                );

                await dispatch("getVardplatsbestallningById", {
                    id: response.data.id,
                    addAsUpdate: true,
                });

                // Hämta senaste slutenvårdbesök kopplat till beställning, då det kan ha ändrats i BE
                if (
                    (rootGetters["ability/can"] as AbilityCan)({
                        action: "read",
                        subject: "slutenvardsbesok",
                    }) &&
                    response?.data?.slutenvardsbesok
                ) {
                    await dispatch(
                        "slutenvardsbesok/getSlutenvardsbesokById",
                        {
                            id: response.data.slutenvardsbesok,
                            addAsUpdate: true,
                        },
                        { root: true }
                    );
                }
            } catch (error) {
                console.error(error);

                throw error;
            }
        },

        partialUpdateVardplatsbestallning: async (
            { commit, state, dispatch, rootGetters }: BaseActionsProps<State>,
            {
                vardplatsbestallning,
            }: {
                vardplatsbestallning: Partial<Vardplatsbestallning>;
            }
        ) => {
            try {
                const response = await klinikenApi.patch(
                    `slutenvard/vardplatsbestallningar/${vardplatsbestallning.id}/`,
                    vardplatsbestallning,
                    getConfig({})
                );

                await dispatch("getVardplatsbestallningById", {
                    id: response.data.id,
                    addAsUpdate: true,
                });

                // Hämta senaste slutenvårdbesök kopplat till beställning, då det kan ha ändrats i BE
                if (
                    (rootGetters["ability/can"] as AbilityCan)({
                        action: "read",
                        subject: "slutenvardsbesok",
                    }) &&
                    response?.data?.slutenvardsbesok
                ) {
                    await dispatch(
                        "slutenvardsbesok/getSlutenvardsbesokById",
                        {
                            id: response.data.slutenvardsbesok,
                            addAsUpdate: true,
                        },
                        { root: true }
                    );
                }
            } catch (error) {
                console.error(error);

                throw error;
            }
        },

        strykVardplatsbestallning: async (
            { commit, state, dispatch, rootGetters }: BaseActionsProps<State>,
            {
                vardplatsbestallning,
            }: {
                vardplatsbestallning: Partial<VardplatsbestallningStryk>;
            }
        ) => {
            try {
                const response = await klinikenApi.patch(
                    `slutenvard/vardplatsbestallningar/${vardplatsbestallning.id}/stryk/`,
                    vardplatsbestallning,
                    getConfig({})
                );

                await dispatch("getVardplatsbestallningById", {
                    id: response.data.id,
                    addAsUpdate: true,
                });

                // Hämta senaste slutenvårdbesök kopplat till beställning, då det kan ha ändrats i BE
                if (
                    (rootGetters["ability/can"] as AbilityCan)({
                        action: "read",
                        subject: "slutenvardsbesok",
                    }) &&
                    response?.data?.slutenvardsbesok
                ) {
                    await dispatch(
                        "slutenvardsbesok/getSlutenvardsbesokById",
                        {
                            id: response.data.slutenvardsbesok,
                            addAsUpdate: true,
                        },
                        { root: true }
                    );
                }
            } catch (error) {
                console.error(error);

                throw error;
            }
        },
    },
};
